/*
 * SonarQube
 * Copyright (C) 2009-2018 SonarSource SA
 * mailto:info AT sonarsource DOT com
 *
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU Lesser General Public
 * License as published by the Free Software Foundation; either
 * version 3 of the License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the GNU
 * Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * along with this program; if not, write to the Free Software Foundation,
 * Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 */
import * as React from 'react';
import { translate } from '../helpers/l10n';
import { get, save } from '../helpers/storage';

type ReactComponent<P> = React.ComponentClass<P> | React.StatelessComponent<P>;

interface Loader<P> {
  (): Promise<{ default: ReactComponent<P> }>;
}

export const LAST_FAILED_CHUNK_STORAGE_KEY = 'sonarqube.last_failed_chunk';

export function lazyLoad<P>(loader: Loader<P>) {
  interface ImportError {
    request?: string;
  }

  interface State {
    Component?: ReactComponent<P>;
    error?: ImportError;
  }

  // use `React.Component`, not `React.PureComponent` to always re-render
  // and let the child component decide if it needs to change
  return class LazyLoader extends React.Component<any, State> {
    mounted = false;
    state: State = {};

    componentDidMount() {
      this.mounted = true;
      loader().then(i => this.receiveComponent(i.default), this.failToReceiveComponent);
    }

    componentWillUnmount() {
      this.mounted = false;
    }

    receiveComponent = (Component: ReactComponent<P>) => {
      if (this.mounted) {
        this.setState({ Component, error: undefined });
      }
    };

    failToReceiveComponent = (error?: ImportError) => {
      const lastFailedChunk = get(LAST_FAILED_CHUNK_STORAGE_KEY);
      if (error && error.request === lastFailedChunk) {
        // BOOM!
        // this is the second time we try to load the same file
        // usually that means the file does not exist on the server
        // so we should not try to reload the page to not fall into infinite reloading
        // just show the error message
        if (this.mounted) {
          this.setState({ Component: undefined, error });
        }
      } else {
        if (error && error.request) {
          save(LAST_FAILED_CHUNK_STORAGE_KEY, error.request);
        }
        window.location.reload();
      }
    };

    render() {
      const { Component, error } = this.state;

      if (error && error.request) {
        return <div className="alert alert-danger">{translate('default_error_message')}</div>;
      }

      if (!Component) {
        return null;
      }

      return <Component {...this.props} />;
    }
  };
}
